import { API } from "@swivl-mono/api-helper";
import { filterClasses, resolveBraceVars, snowyVarCatalog } from "@swivl-mono/helpers";
import {
  BotStarterOpeningScope,
  BotStarterOpeningView,
  CAMPAIGN_MAX_MESSAGES,
  CAMPAIGN_MESSAGE_MAX_CHARS,
  LocationScope,
  LocationVarInheritance,
  LocationVarKey
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Spinner, Switch, Tooltip, VariableTypeahead } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { GrRevert } from "react-icons/gr";

import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { CortexInheritancePill } from "../../../Views/Properties/CortexV3/Components/CortexInheritancePill";

/**
 * Conversation Starters — how each one opens, per scope (ENG-3108; plan
 * `docs/snowy/plans/2026-08-30-starter-openings-operator-control.md`).
 *
 * Each starter is a row with one toggle: Automatic (the bot writes the opening from the starter's
 * context) or Verbatim (send exactly this text). Turn Verbatim on and the text appears. Cascades
 * org → group → location like every Cortex setting, with the same pills and revert.
 *
 * Not a Cortex-variable page: the list of starters is dynamic, so the data comes from its own
 * endpoint and the page fetches for itself, the way the Admin page does.
 */

export const PAGE_KEYS: LocationVarKey[] = [];

const FROM_TO_PILL: Record<BotStarterOpeningScope, LocationVarInheritance> = {
  location: LocationVarInheritance.NONE,
  group: LocationVarInheritance.GROUP,
  org: LocationVarInheritance.ORG,
  starter: LocationVarInheritance.DEFAULT
};

/** What the pill says for a row at this scope: customized here, or inherited from where. */
function pillFor(row: BotStarterOpeningView, scopeType: LocationScope): LocationVarInheritance {
  if (row.customizedHere) return LocationVarInheritance.NONE;
  if (row.from === "starter") return LocationVarInheritance.DEFAULT;
  // An org row seen from org scope is "customized here", handled above; seen from below it is "From Org".
  if (row.from === "org") return scopeType === LocationScope.ORG ? LocationVarInheritance.NONE : LocationVarInheritance.ORG;
  return FROM_TO_PILL[row.from];
}

/** Read mode shows `{{Type:Key}}` tokens by their display names, the way the editor's pills do — never raw machinery. */
function displayText(message: string): string {
  const displays = Object.fromEntries(snowyVarCatalog().map((tag) => [tag.id, `{${tag.display}}`]));
  return resolveBraceVars(message, displays).text.trim();
}

interface Draft {
  useVerbatim: boolean;
  messages: string[];
}

export default function ConversationStartersPage(): React.ReactElement {
  const page = usePropertiesGetLocationIds();
  const orgId = Member.orgId;
  const scope = { groupId: page.groupId, locationId: page.locationId };

  const [rows, setRows] = useState<BotStarterOpeningView[] | null>(null);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!orgId) return;
    try {
      setRows(await API.StarterOpenings.view(orgId, scope));
    } catch (err) {
      AlertsModel.errorToast(err instanceof Error && err.message ? err.message : "Could not load Conversation Starters");
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, page.groupId, page.locationId]);

  useEffect(() => {
    setRows(null);
    setEditingSlug(null);
    setDraft(null);
    void load();
  }, [load]);

  const handleEdit = (row: BotStarterOpeningView): void => {
    setEditingSlug(row.starterSlug);
    setDraft({ useVerbatim: row.useVerbatim, messages: row.messages.length > 0 ? row.messages : [""] });
  };

  const handleCancel = (): void => {
    setEditingSlug(null);
    setDraft(null);
  };

  const handleSave = async (row: BotStarterOpeningView): Promise<void> => {
    if (!orgId || !draft) return;
    setSaving(true);
    try {
      const messages = draft.messages.map((m) => m.trim()).filter((m) => m.length > 0);
      const next = await API.StarterOpenings.upsert(orgId, row.starterSlug, { ...scope, useVerbatim: draft.useVerbatim, messages });
      setRows(next);
      handleCancel();
    } catch (err) {
      AlertsModel.errorToast(err instanceof Error && err.message ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (row: BotStarterOpeningView): Promise<void> => {
    if (!orgId) return;
    const to = row.inherited.useVerbatim ? `Verbatim: “${row.inherited.messages[0] ?? ""}”` : "Automatic";
    if (!window.confirm(`Revert this starter here? It will go back to ${to}.`)) return;
    setSaving(true);
    try {
      setRows(await API.StarterOpenings.remove(orgId, row.starterSlug, scope));
      handleCancel();
    } catch (err) {
      AlertsModel.errorToast(err instanceof Error && err.message ? err.message : "Could not revert");
    } finally {
      setSaving(false);
    }
  };

  const draftValid =
    !!draft &&
    (!draft.useVerbatim || (draft.messages.some((m) => m.trim().length > 0) && draft.messages.every((m) => m.length <= CAMPAIGN_MESSAGE_MAX_CHARS)));

  return (
    <div className="flex flex-col gap-4">
      <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
        <div className="px-4 py-3">
          <h3 className="heading-base-alt text-grey-900">Conversation Starters</h3>
          <p className="body-sm mt-0.5 text-grey-600">
            How each starter opens a conversation. Automatic: the bot writes the opening. Verbatim: it sends exactly the text you write.
          </p>
        </div>

        <div className="border-t border-grey-200">
          {rows === null && (
            <div className="p-4">
              <Spinner />
            </div>
          )}
          {rows !== null && rows.length === 0 && (
            <p className="body-sm p-4 text-grey-600">No Conversation Starters are published for this org yet.</p>
          )}
          {rows?.map((row) => {
            const isEditing = editingSlug === row.starterSlug;
            const shown: Draft = isEditing && draft ? draft : { useVerbatim: row.useVerbatim, messages: row.messages };
            return (
              <div key={row.starterSlug} className="mx-4 flex flex-col gap-2 border-b border-grey-200 py-3 last:border-b-0">
                <div className="grid grid-cols-[200px_minmax(0,1fr)_auto] items-center gap-x-4">
                  <div className="min-w-0">
                    <span className="body-sm-alt block truncate text-grey-900">{row.label}</span>
                    <span className="body-xs text-grey-500">{row.channels.join(" · ")}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      on={shown.useVerbatim}
                      readOnly={!isEditing}
                      activeColor="orange"
                      onSet={(on) => setDraft({ useVerbatim: on, messages: shown.messages.length > 0 ? shown.messages : [""] })}
                    />
                    <span className={filterClasses("body-sm", shown.useVerbatim ? "text-grey-900" : "text-grey-600")}>
                      {shown.useVerbatim ? "Verbatim — sends exactly this text" : "Automatic — the bot writes the opening"}
                    </span>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <CortexInheritancePill inheritance={pillFor(row, page.type)} />
                    {isEditing ? (
                      <>
                        {row.customizedHere && (
                          <Tooltip content="Go back to what this starter inherits">
                            <button
                              type="button"
                              onClick={() => void handleRevert(row)}
                              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
                            >
                              Revert
                              <GrRevert className="size-4" />
                            </button>
                          </Tooltip>
                        )}
                        <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button
                          color={ButtonColor.ORANGE}
                          size={ButtonSize.SMALL}
                          disabled={saving || !draftValid}
                          onClickAsync={() => handleSave(row)}
                        >
                          {saving ? "Saving…" : "Save"}
                        </Button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={editingSlug !== null}
                        onClick={() => handleEdit(row)}
                        className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500 disabled:cursor-default disabled:text-grey-400"
                      >
                        Edit
                        <BiEditAlt className="size-4" />
                      </button>
                    )}
                  </div>
                </div>

                {shown.useVerbatim && (
                  <div className="col-start-2 flex flex-col gap-2 pl-[216px]">
                    {isEditing && draft ? (
                      <>
                        {draft.messages.map((m, i) => (
                          <VariableTypeahead
                            key={i}
                            label={draft.messages.length > 1 ? `Message ${i + 1}` : undefined}
                            value={m}
                            maxLength={CAMPAIGN_MESSAGE_MAX_CHARS}
                            placeholder="Type {{ to insert a variable"
                            onChange={(next) => setDraft({ ...draft, messages: draft.messages.map((x, j) => (j === i ? next : x)) })}
                          />
                        ))}
                        {draft.messages.length < CAMPAIGN_MAX_MESSAGES && (
                          <Button
                            size={ButtonSize.SMALL}
                            variant={ButtonVariant.GHOST}
                            onClick={() => setDraft({ ...draft, messages: [...draft.messages, ""] })}
                          >
                            + Add message
                          </Button>
                        )}
                        <p className="body-sm text-grey-500">
                          Sent word for word. A customer missing a value for a variable is skipped rather than sent broken text.
                        </p>
                      </>
                    ) : (
                      shown.messages.map((m, i) => (
                        <p key={i} className="body-sm rounded-md border border-grey-200 bg-grey-50 px-3 py-2 text-grey-800">
                          {displayText(m)}
                        </p>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
